import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useAppSelector } from '../../store/hooks';
import { vedSourcerChatAPI, VedSourcerChatMessage, VedSourcerChatThread } from '../../services/chatApi';

function mentionQuery(text: string) {
  const m = String(text || '').match(/@([^\s@]*)$/);
  return m ? m[1] : null;
}

function fileFromClipboard(data: DataTransfer | null) {
  if (!data) return null;
  const items = data.items;
  if (items) {
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === 'file') return items[i].getAsFile();
    }
  }
  return data.files?.[0] || null;
}

export default function VedSourcerChatPane({
  initialOrderId = null,
  pageMode = false,
  embedded = false,
  onSelectOrder
}: {
  initialOrderId?: number | null;
  pageMode?: boolean;
  embedded?: boolean;
  onSelectOrder?: (id: number) => void;
}) {
  const myId = String(useAppSelector((s) => s.auth.user?.id) || '').toLowerCase();
  const [threads, setThreads] = useState<VedSourcerChatThread[]>([]);
  const [orderId, setOrderId] = useState<number | null>(initialOrderId ? Number(initialOrderId) : null);
  const [messages, setMessages] = useState<VedSourcerChatMessage[]>([]);
  const [headerArticle, setHeaderArticle] = useState('');
  const [draft, setDraft] = useState('');
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<Array<{ id: string; label: string; role?: string | null }>>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (initialOrderId) setOrderId(Number(initialOrderId));
  }, [initialOrderId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await vedSourcerChatAPI.getThreads();
        if (cancelled) return;
        const list = Array.isArray(data?.threads) ? data.threads : [];
        setThreads(list);
        setLoadingThreads(false);
        if (list[0]?.order_id) {
          setOrderId((cur) => (cur ? cur : Number(list[0].order_id)));
        }
      } catch {
        if (!cancelled) {
          setThreads([]);
          setLoadingThreads(false);
        }
      }
    };
    setLoadingThreads(true);
    void load();
    const timer = window.setInterval(() => { void load(); }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!orderId) {
      setMessages([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await vedSourcerChatAPI.getMessages(orderId);
        if (cancelled) return;
        setMessages(Array.isArray(data?.messages) ? data.messages : []);
        setHeaderArticle(data?.internal_article || '');
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setLoadingMsgs(false);
      }
    };
    setLoadingMsgs(true);
    void load();
    void vedSourcerChatAPI.markRead(orderId).catch(() => {});
    const timer = window.setInterval(() => { void load(); }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [orderId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, orderId]);

  useEffect(() => {
    const q = mentionQuery(draft);
    if (q == null) {
      setCandidates([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const data = await vedSourcerChatAPI.mentionCandidates(q);
        if (!cancelled) setCandidates(Array.isArray(data?.users) ? data.users : []);
      } catch {
        if (!cancelled) setCandidates([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [draft]);

  function selectThread(id: number) {
    const next = Number(id);
    setOrderId(next);
    onSelectOrder?.(next);
  }

  function pickMention(user: { id: string; label: string }) {
    setDraft((prev) => prev.replace(/@([^\s@]*)$/, `@${user.label} `));
    setMentionedIds((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    setCandidates([]);
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if ((!body && !file) || !orderId) return;
    setSending(true);
    try {
      const row = await vedSourcerChatAPI.postMessage(orderId, {
        body,
        mentioned_user_ids: mentionedIds,
        file
      });
      setDraft('');
      setMentionedIds([]);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setMessages((prev) => [...prev, row]);
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  }

  const previewUrl = file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

  return (
    <div className={`ved-sourcer-chat-pane${pageMode ? ' is-page' : ''}${embedded ? ' is-embedded' : ''}`}>
      <aside className="ved-sourcer-chat-list">
        <div className="ved-sourcer-chat-list-title">Buyers</div>
        {loadingThreads && threads.length === 0 ? (
          <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : null}
        {threads.length === 0 && !loadingThreads ? (
          <div className="text-muted small px-2 py-2">Нет чатов. Появятся, когда вас тегнут.</div>
        ) : null}
        {threads.map((th) => {
          const active = Number(th.order_id) === Number(orderId);
          const unread = Number(th.unread_count) || 0;
          const mentions = Number(th.mention_unread_count) || 0;
          return (
            <button
              key={th.order_id}
              type="button"
              className={`ved-sourcer-chat-thread${active ? ' is-active' : ''}`}
              onClick={() => selectThread(th.order_id)}
            >
              <div className="d-flex justify-content-between gap-2">
                <span className="fw-semibold">{th.internal_article || `#${th.order_id}`}</span>
                {mentions > 0 ? <span className="ved-sourcer-chat-mention-badge">@</span> : null}
                {unread > 0 ? <span className="ved-sourcer-chat-unread">{unread > 99 ? '99+' : unread}</span> : null}
              </div>
              <div className="small text-muted text-truncate">{th.last_body || '—'}</div>
            </button>
          );
        })}
      </aside>
      <section
        className="ved-sourcer-chat-main"
        onPaste={(e) => {
          const pasted = fileFromClipboard(e.clipboardData);
          if (pasted) {
            e.preventDefault();
            setFile(pasted);
          }
        }}
      >
        <div className="ved-sourcer-chat-header">
          <div className="fw-semibold">Buyers{headerArticle ? ` · ${headerArticle}` : ''}</div>
        </div>
        <div className="ved-sourcer-chat-log">
          {!orderId ? <div className="text-muted small p-3">Нет чатов</div> : null}
          {loadingMsgs && messages.length === 0 ? (
            <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : null}
          {messages.map((m) => {
            const mine = String(m.sender_user_id || '').toLowerCase() === myId;
            const mentionedMe = (m.mentioned_user_ids || []).includes(myId);
            return (
              <div key={m.id} className={`ved-sourcer-chat-msg${mine ? ' is-mine' : ''}${mentionedMe ? ' is-mention' : ''}`}>
                <div className="small text-muted">{m.sender_label || '—'}</div>
                {m.body ? <div>{m.body}</div> : null}
                {m.attachment_url ? (
                  m.attachment_mime?.startsWith('image/') ? (
                    <a href={m.attachment_url} target="_blank" rel="noreferrer">
                      <img className="ved-sourcer-chat-img" src={m.attachment_url} alt={m.attachment_name || ''} />
                    </a>
                  ) : (
                    <a href={m.attachment_url} target="_blank" rel="noreferrer">📎 {m.attachment_name || 'file'}</a>
                  )
                ) : null}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <Form onSubmit={send} className="ved-sourcer-chat-composer">
          {file ? (
            <div className="ved-sourcer-chat-pending">
              {previewUrl ? <img src={previewUrl} alt="" /> : <span>📎 {file.name}</span>}
              <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}>×</button>
            </div>
          ) : null}
          {candidates.length > 0 ? (
            <div className="ved-sourcer-chat-mentions">
              {candidates.map((u) => (
                <button key={u.id} type="button" onClick={() => pickMention(u)}>
                  {u.label}
                  {u.role ? <span className="text-muted"> · {u.role}</span> : null}
                </button>
              ))}
            </div>
          ) : null}
          <div className="d-flex gap-2">
            <input ref={fileRef} type="file" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <Button type="button" variant="outline-secondary" disabled={!orderId} onClick={() => fileRef.current?.click()}>
              📎
            </Button>
            <Form.Control
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={4000}
              disabled={!orderId}
              placeholder="Сообщение, @имя"
            />
            <Button type="submit" disabled={!orderId || sending || (!draft.trim() && !file)}>
              {sending ? <Spinner animation="border" size="sm" /> : 'Отправить'}
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
}
